import { AppDefinitionId, WidgetId } from '@wix/members-area-app-definitions';
import type { ComponentRef, EditorSDK } from '@wix/platform-editor-sdk';
import eventually from 'wix-eventually';

import { APP_TOKEN } from '../../../constants';
import {
  getAllCompsByAppDefinitionId,
  getStyleParams,
  getTpaData,
  parseStyleParamsForSetter,
  parseTpaDataParamsForSetter,
} from '../../../wrappers/tpa';

const LOG_PREFIX = '[MA v3 migration][widget-settings]';

type WidgetPointer = { appDefinitionId: string; widgetId: string };

type StyleParamsResult = Awaited<ReturnType<typeof getStyleParams>>;
type TpaDataResult = Awaited<ReturnType<typeof getTpaData>>;

type CapturedWidgetSettings = {
  pointer: WidgetPointer;
  styleParams: StyleParamsResult;
  tpaData: TpaDataResult;
};

const MA_WIDGET_IDS = new Set<string>(Object.values(WidgetId));
const MA_APP_DEF_IDS = Array.from(new Set(Object.values(AppDefinitionId) as string[]));

const isMemberAreaWidget = (widgetId?: string): widgetId is string =>
  !!widgetId && MA_WIDGET_IDS.has(widgetId);

// discover the member-area widgets actually installed on the site before uninstall
const discoverInstalledWidgets = async (editorSDK: EditorSDK): Promise<WidgetPointer[]> => {
  const pointers: WidgetPointer[] = [];
  const seen = new Set<string>();

  await Promise.all(
    MA_APP_DEF_IDS.map(async (appDefinitionId) => {
      try {
        const comps = await getAllCompsByAppDefinitionId({ editorSDK, appDefinitionId });
        (comps ?? []).forEach((comp: { widgetId?: string }) => {
          if (!isMemberAreaWidget(comp.widgetId)) {
            return;
          }

          const key = `${appDefinitionId}/${comp.widgetId}`;
          if (seen.has(key)) {
            return;
          }

          seen.add(key);
          pointers.push({ appDefinitionId, widgetId: comp.widgetId });
        });
      } catch {
        // app not installed / not queryable - ignore
      }
    }),
  );

  return pointers;
};

// `setStyleParams` / `tpa.data.set` apply their effect immediately, but the returned
// promise only resolves once editorReady unwinds - awaiting it here would deadlock the
// migration (which itself runs inside editorReady). So we dispatch without awaiting.
const dispatchWithoutAwaiting = (label: string, promise: Promise<unknown>): void => {
  promise.catch((e) => console.error(`${LOG_PREFIX} ${label} failed`, e));
};

const getWidgetCompRef = async (
  editorSDK: EditorSDK,
  { appDefinitionId, widgetId }: WidgetPointer,
): Promise<ComponentRef | null> => {
  const comps = await getAllCompsByAppDefinitionId({ editorSDK, appDefinitionId });
  const comp = comps?.find((candidate: { widgetId?: string }) => candidate?.widgetId === widgetId);

  if (!comp?.id) {
    return null;
  }

  return editorSDK.components.getById(APP_TOKEN, { id: comp.id });
};

export const captureWidgetsSettings = async (editorSDK: EditorSDK): Promise<CapturedWidgetSettings[]> => {
  const widgets = await discoverInstalledWidgets(editorSDK);
  const captured: CapturedWidgetSettings[] = [];

  for (const pointer of widgets) {
    try {
      const compRef = await getWidgetCompRef(editorSDK, pointer);

      if (!compRef) {
        continue;
      }

      const [styleParams, tpaData] = await Promise.all([
        getStyleParams(editorSDK, compRef),
        getTpaData(editorSDK, compRef),
      ]);

      captured.push({ pointer, styleParams, tpaData });
    } catch (e) {
      console.error(`${LOG_PREFIX} failed to capture settings for ${pointer.widgetId}`, e);
    }
  }

  return captured;
};

const resolveV3WidgetCompRef = async (editorSDK: EditorSDK, pointer: WidgetPointer): Promise<ComponentRef | null> => {
  // The V3 widget plugin may be installed asynchronously as part of the V3 install,
  // so poll until its component ref is resolvable before applying the saved settings.
  let compRef: ComponentRef | null = null;

  try {
    await eventually(
      async () => {
        compRef = await getWidgetCompRef(editorSDK, pointer);
        if (!compRef) {
          throw new Error(`${LOG_PREFIX} V3 widget ${pointer.widgetId} not available yet`);
        }
      },
      { timeout: 15000, interval: 500 },
    );
  } catch (e) {
    console.error(`${LOG_PREFIX} could not resolve V3 widget ${pointer.widgetId}`, e);
    return null;
  }

  return compRef;
};

const restoreWidgetSettings = async (
  editorSDK: EditorSDK,
  { pointer, styleParams, tpaData }: CapturedWidgetSettings,
) => {
  const compRef = await resolveV3WidgetCompRef(editorSDK, pointer);

  if (!compRef) {
    return;
  }

  try {
    const mappedParams = parseStyleParamsForSetter(styleParams);
    dispatchWithoutAwaiting(
      `setStyleParams ${pointer.widgetId}`,
      editorSDK.tpa.setStyleParams(APP_TOKEN, { compRef, styleParams: mappedParams }),
    );
  } catch (e) {
    console.error(`${LOG_PREFIX} failed to restore style params for ${pointer.widgetId}`, e);
  }

  try {
    const componentDataParams = tpaData.COMPONENT ?? {};
    const mappedDatas = parseTpaDataParamsForSetter(componentDataParams, compRef);
    dispatchWithoutAwaiting(
      `tpa.data.set ${pointer.widgetId}`,
      Promise.all(mappedDatas.map((data) => editorSDK.tpa.data.set(APP_TOKEN, data))),
    );
  } catch (e) {
    console.error(`${LOG_PREFIX} failed to restore tpa data for ${pointer.widgetId}`, e);
  }
};

export const restoreWidgetsSettings = async (editorSDK: EditorSDK, captured: CapturedWidgetSettings[]) => {
  for (const widgetSettings of captured) {
    try {
      await restoreWidgetSettings(editorSDK, widgetSettings);
    } catch (e) {
      console.error(`${LOG_PREFIX} failed to restore settings for ${widgetSettings.pointer.widgetId}`, e);
    }
  }
};
